import { useState } from "react";

export type SubmitStatus = "idle" | "submitting" | "success" | "error";

interface UseEnquirySubmitResult {
  status: SubmitStatus;
  message: string;
  submit: (formEl: HTMLFormElement, submitAddr: string, honeypotName?: string) => Promise<void>;
  reset: () => void;
}

const DEFAULT_SUCCESS = "Thank you. Your enquiry has been received.";

export function useEnquirySubmit(): UseEnquirySubmitResult {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [message, setMessage] = useState("");

  const reset = () => {
    setStatus("idle");
    setMessage("");
  };

  const submit = async (
    formEl: HTMLFormElement,
    submitAddr: string,
    honeypotName = "website_alt",
  ) => {
    const formData = new FormData(formEl);
    const honeypotValue = String(formData.get(honeypotName) ?? "").trim();

    // Client-side honeypot hit: behave like a normal success, send nothing.
    if (honeypotValue) {
      setStatus("success");
      setMessage(DEFAULT_SUCCESS);
      return;
    }

    formData.delete(honeypotName);

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        params.append(key, value);
      }
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const record = parsed as
        | { code?: string; message?: string; meta?: { message?: string; detail?: string } }
        | null;

      const serverMsg =
        record?.meta?.message || record?.message || record?.meta?.detail || responseText;
      const code = record?.code;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "Submission failed. Please check your details and try again.",
        );
        return;
      }

      setStatus("success");
      setMessage(DEFAULT_SUCCESS);
      formEl.reset();
    } catch (err) {
      setStatus("error");
      setMessage(
        err instanceof Error
          ? `Network error: ${err.message}`
          : "Network error. Please try again in a moment.",
      );
    }
  };

  return { status, message, submit, reset };
}